import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled } from '@mui/material/styles';
import { getEncounterDifficulty } from '../../utils/encounterUtils';
import TitleSubtitleChip from './TitleSubtitleChip';

const DifficultyBarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 140,
}));

const DifficultyBarTrack = styled(Box)(({ theme }) => ({
  flex: 1,
  height: 8,
  backgroundColor: theme.palette.background.dark,
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
}));

const DifficultyLabel = styled(Typography)(({ theme, color }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: color,
  textTransform: 'capitalize',
  minWidth: 45,
  textAlign: 'right',
}));

function getDifficultyColor(difficulty) {
  switch (difficulty) {
    case 'trivial':
      return '#888888';
    case 'easy':
      return '#4CAF50';
    case 'medium':
      return '#FF9800';
    case 'hard':
      return '#F44336';
    case 'deadly':
      return '#9C27B0';
    default:
      return '#888888';
  }
}

/**
 * Visual difficulty bar for an encounter vs party (uses adjusted XP).
 * Pass either (encounter + partySize + partyLevel) or (adjustedXP + levels).
 *
 * When party comes from DM defaults (no PC tokens), pass `partyFallbackSummary` and `onPartySettingsClick`
 * to show size/level labels and a settings control below the bar.
 */
export default function EncounterDifficultyBar({
  encounter,
  partySize,
  partyLevel,
  adjustedXP: adjustedXPProp,
  levels: levelsProp,
  partyFallbackSummary = null,
  onPartySettingsClick,
  width = '100%',
}) {
  let adjustedXP;
  let levels;
  if (levelsProp != null && levelsProp.length > 0 && adjustedXPProp != null) {
    adjustedXP = adjustedXPProp;
    levels = levelsProp;
  } else if (encounter != null && partySize > 0 && partyLevel != null) {
    adjustedXP = encounter.adjustedXP;
    levels = Array(partySize).fill(partyLevel);
  } else {
    return null;
  }

  const { difficulty, progress } = getEncounterDifficulty(adjustedXP, levels);
  const color = getDifficultyColor(difficulty);
  const widthPct = Math.max(0, Math.min(100, progress));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: width }}>
      <DifficultyBarContainer>
        <DifficultyBarTrack>
          <Box
            sx={{
              height: '100%',
              width: `${widthPct}%`,
              backgroundColor: color,
              borderRadius: 'inherit',
              transition: 'width 0.35s ease, background-color 0.35s ease',
            }}
          />
        </DifficultyBarTrack>
        <DifficultyLabel color={color}>{difficulty}</DifficultyLabel>
      </DifficultyBarContainer>
      {partyFallbackSummary != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 0.5,
            pl: 0.25,
          }}
        >
          <Box display="flex" flexDirection="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
            <TitleSubtitleChip
                title="Party"
                subtitle={partyFallbackSummary.partySize}
                tooltip="Size of the party"
                tooltipProps={{ placement: 'top', arrow: true }}
              />
            <TitleSubtitleChip
                title="Level"
                subtitle={partyFallbackSummary.partyLevel}
                tooltip="Level of the party"
                tooltipProps={{ placement: 'top', arrow: true }}
              />
          </Box>
          {typeof onPartySettingsClick === 'function' && (
            <IconButton
              size="small"
              tooltip='Edit party size and level'
              aria-label="Edit party size and level"
                onClick={onPartySettingsClick}
                sx={{ mt: -0.5, color: 'text.secondary' }}
              >
                <SettingsIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}